import { format, formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { RefreshCw } from "lucide-react";
import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import ConfirmModal from "../../../shared/components/ConfirmModal";
import EmptyState from "../../../shared/components/EmptyState";
import LoadingOverlay from "../../../shared/components/LoadingOverlay";
import PageHeader from "../../../shared/components/PageHeader";
import {
  useDeleteWorkspace,
  useWorkspacesQuery,
} from "../hooks/useWorkspaces";

const WorkspacesPage = () => {
  const [visibilityFilter, setVisibilityFilter] = useState<"all" | "public" | "private">("all");
  const [selectedOwner, setSelectedOwner] = useState("all");
  const [showDeleted, setShowDeleted] = useState<boolean>(false);

  // Modal states
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; workspaceId: string | null }>({
    isOpen: false,
    workspaceId: null,
  });

  const { data: workspaces, isLoading, isFetching, isError, refetch, dataUpdatedAt } = useWorkspacesQuery(showDeleted);
  const deleteMutation = useDeleteWorkspace();

  const owners = useMemo(() => {
    const result = new Map<number, string>();
    for (const workspace of workspaces ?? []) {
      if (workspace.userId !== null) {
        result.set(workspace.userId, workspace.userName || workspace.userEmail || `ID ${workspace.userId}`);
      }
    }
    return [...result].sort((a, b) => a[1].localeCompare(b[1]));
  }, [workspaces]);

  const handleDelete = (workspaceId: string) => {
    setDeleteModal({ isOpen: true, workspaceId });
  };

  const confirmDelete = async () => {
    if (deleteModal.workspaceId) {
      try {
        await deleteMutation.mutateAsync(deleteModal.workspaceId);
      } catch {
        // The mutation error is displayed in the list after the dialog closes.
      }
    }
  };

  const filteredWorkspaces = useMemo(() => {
    let filtered = workspaces ?? [];
    if (visibilityFilter === "public") {
      filtered = filtered.filter((w) => w.isPublic);
    } else if (visibilityFilter === "private") {
      filtered = filtered.filter((w) => !w.isPublic);
    }
    if (selectedOwner !== "all") {
      filtered = filtered.filter((w) => selectedOwner === "unassigned" ? w.userId === null : w.userId === Number(selectedOwner));
    }
    return filtered;
  }, [workspaces, visibilityFilter, selectedOwner]);

  const hasFilters = visibilityFilter !== "all" || selectedOwner !== "all" || showDeleted;

  const clearFilters = () => {
    setVisibilityFilter("all");
    setSelectedOwner("all");
    setShowDeleted(false);
  };

  return (
    <div className="flex min-w-0 flex-col gap-6">
      <PageHeader
        title="Workspaces"
        subtitle="Registro de workspaces en el servidor"
      />

      <Card className="border-neutral-800 bg-neutral-900/50">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <CardTitle className="text-lg font-semibold text-neutral-100">
                Listado de workspaces
              </CardTitle>
              <CardDescription className="text-neutral-400">
                {workspaces
                  ? `${filteredWorkspaces.length} de ${workspaces?.length ?? 0} registros`
                  : isLoading ? "Cargando registros..." : "Listado no disponible"}
                {dataUpdatedAt > 0 && (
                  <span className="mt-1 block text-xs">
                    Última consulta: {format(new Date(dataUpdatedAt), "HH:mm:ss")}
                  </span>
                )}
              </CardDescription>
            </div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="icon" className="shrink-0" aria-label="Actualizar workspaces" onClick={() => void refetch()} disabled={isFetching}>
                    <RefreshCw className={`size-4 ${isFetching ? "animate-spin" : ""}`} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Actualizar workspaces</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {isError && (
            <p role="alert" className="text-sm text-red-400">
              {workspaces ? "No se pudo actualizar el listado. Se muestra la última consulta disponible." : "No se pudieron cargar los workspaces. Volvé a intentar con Actualizar."}
            </p>
          )}
          {deleteMutation.isError && (
            <p role="alert" className="text-sm text-red-400">No se pudo eliminar el registro. Volvé a intentarlo.</p>
          )}
          {/* Filters */}
          <div className="flex flex-wrap gap-4">
            <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center">
              <Label htmlFor="workspace-visibility" className="text-xs text-neutral-400">Visibilidad:</Label>
              <Select
                value={visibilityFilter}
                onValueChange={(v) => setVisibilityFilter(v as typeof visibilityFilter)}
              >
                <SelectTrigger id="workspace-visibility" className="h-8 w-[120px] border-neutral-700 bg-neutral-800/50 text-xs text-neutral-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-neutral-700 bg-neutral-900">
                  <SelectItem value="all" className="text-neutral-200">
                    Todos
                  </SelectItem>
                  <SelectItem value="public" className="text-neutral-200">
                    Públicos
                  </SelectItem>
                  <SelectItem value="private" className="text-neutral-200">
                    Privados
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center">
              <Label htmlFor="workspace-owner" className="text-xs text-neutral-400">Propietario:</Label>
              <Select
                value={selectedOwner}
                onValueChange={setSelectedOwner}
              >
                <SelectTrigger id="workspace-owner" className="h-8 w-[180px] border-neutral-700 bg-neutral-800/50 text-xs text-neutral-200">
                  <SelectValue placeholder="Todos los usuarios" />
                </SelectTrigger>
                <SelectContent className="border-neutral-700 bg-neutral-900">
                  <SelectItem value="all" className="text-neutral-200">
                    Todos los usuarios
                  </SelectItem>
                  <SelectItem value="unassigned" className="text-neutral-200">Sin propietario</SelectItem>
                  {owners.map(([id, label]) => (
                    <SelectItem
                      key={id}
                      value={id.toString()}
                      className="text-neutral-200"
                    >
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center">
              <Label htmlFor="workspace-status" className="text-xs text-neutral-400">Estado:</Label>
              <Select
                value={showDeleted ? "all" : "active"}
                onValueChange={(v) => setShowDeleted(v === "all")}
              >
                <SelectTrigger id="workspace-status" className="h-8 w-[180px] border-neutral-700 bg-neutral-800/50 text-xs text-neutral-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-neutral-700 bg-neutral-900">
                  <SelectItem value="active" className="text-neutral-200">
                    No eliminados
                  </SelectItem>
                  <SelectItem value="all" className="text-neutral-200">
                    Todos (incluye eliminados)
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {hasFilters && (
              <Button
                variant="outline"
                size="sm"
                onClick={clearFilters}
                className="border-neutral-700 text-neutral-300 hover:bg-neutral-700"
              >
                Limpiar filtros
              </Button>
            )}
          </div>

          {/* Table */}
          {filteredWorkspaces.length > 0 && (
            <Table className="min-w-[1040px]">
              <TableHeader>
                <TableRow className="border-neutral-800 hover:bg-transparent">
                  <TableHead className="text-neutral-400">Nombre</TableHead>
                  <TableHead className="text-neutral-400">Propietario</TableHead>
                  <TableHead className="text-neutral-400">Visibilidad</TableHead>
                  <TableHead className="text-neutral-400">Creado</TableHead>
                  <TableHead className="text-neutral-400">Actualizado</TableHead>
                  <TableHead className="text-right text-neutral-400">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredWorkspaces.map((workspace) => {
                  return (
                    <TableRow
                      key={workspace.id}
                      className="border-neutral-800 hover:bg-neutral-800/50"
                    >
                      <TableCell className="min-w-[272px] max-w-[360px] break-words">
                        <div className="flex flex-col gap-0.5">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-neutral-100">{workspace.name}</span>
                            {workspace.deletedAt && (
                              <Badge className="bg-red-500/20 text-red-400">Eliminado</Badge>
                            )}
                            {workspace.bannedAt && (
                              <Badge className="bg-yellow-500/20 text-yellow-400">Baneado</Badge>
                            )}
                          </div>
                          <span className="font-mono text-xs text-neutral-500 whitespace-nowrap">{workspace.id}</span>
                          {workspace.description && (
                            <span className="text-xs text-neutral-500 line-clamp-1">
                              {workspace.description}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="min-w-[220px] max-w-[300px] break-words text-neutral-300">
                        {workspace.userId !== null ? (
                          <div className="flex flex-col gap-0.5">
                            <span>{workspace.userName || workspace.userEmail || `ID ${workspace.userId}`}</span>
                            <span className="text-xs text-neutral-500 break-all">
                              {workspace.userName && workspace.userEmail ? `${workspace.userEmail} · ` : ""}ID {workspace.userId}
                            </span>
                          </div>
                        ) : (
                          <span className="text-neutral-500">Sin propietario</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {workspace.isPublic ? (
                          <Badge className="bg-purple-500/20 text-purple-400">Público</Badge>
                        ) : (
                          <Badge variant="outline" className="border-neutral-600 text-neutral-400">
                            Privado
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-neutral-400">
                        <time dateTime={workspace.createdAt} title={format(new Date(workspace.createdAt), "dd/MM/yyyy HH:mm:ss")}>
                          {format(new Date(workspace.createdAt), "dd/MM/yyyy HH:mm")}
                        </time>
                      </TableCell>
                      <TableCell className="text-neutral-400" title={format(new Date(workspace.updatedAt), "dd/MM/yyyy HH:mm:ss")}>
                        {formatDistanceToNow(new Date(workspace.updatedAt), {
                          addSuffix: true,
                          locale: es,
                        })}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(workspace.id)}
                          disabled={deleteMutation.isPending || !!workspace.deletedAt}
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                        >
                          Eliminar
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}

          {isLoading && <LoadingOverlay />}

          {!isLoading && !isError && filteredWorkspaces.length === 0 && workspaces && workspaces.length > 0 && (
            <EmptyState
              title="Sin resultados"
              description="No se encontraron workspaces con los filtros aplicados."
            />
          )}

          {!isLoading && !isError && workspaces && workspaces.length === 0 && (
            <EmptyState
              title="Sin workspaces"
              description="No hay registros de workspaces en el servidor."
            />
          )}
        </CardContent>
      </Card>

      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, workspaceId: null })}
        onConfirm={confirmDelete}
        title="Eliminar registro de workspace"
        message="Se marcará como eliminado en el servidor. Los archivos locales no se borrarán."
        confirmText="Eliminar"
        confirmVariant="danger"
      />
    </div>
  );
};

export default WorkspacesPage;
